import { PaymentsProp } from '@wetags/payments';
import { PaymentsDeprecatedOrg, PaymentsDeprecatedProp } from '@wetags/payments-deprecated';
import { useMerchants } from '@frontend/payments-hooks';
import { useCareCreditLocationProviderStatus } from './use-care-credit-location-provider-status';

/**
 * Care Credit (CC) & Payments Possible States
 *
 * Controlled by wetags: `care-credit-enabled` and `care-credit-decoupled`.
 *
 * | # | Payments | Care Credit | Active WE Tag           | Output Flags                                          | Context / Notes                  |
 * |:-:|:--------:|:-----------:|:-----------------------|:-------------------------------------------------------|:---------------------------------|
 * | 1 |    ON    |     OFF     | *(none)*               | `hasPayments: true`,  `showCareCreditPayments: false`  | Current DCA behavior             |
 * | 2 |    ON    |     ON      | `care-credit-enabled`  | `hasPayments: true`,  `showCareCreditPayments: true`   | Most payments-enabled locations  |
 * | 3 |   OFF    |     ON      | `care-credit-decoupled`| `hasPayments: false`, `showCareCreditDecoupled: true`  | Decoupled CC only                |
 * | 4 |   OFF    |     OFF     | *(none)*               | `hasPayments: false`, `showCareCreditPayments: false`  | All payment features disabled    |
 */

type UseCareCreditLocationStatusOptios = {
  enabled?: boolean;
};
export const useCareCreditLocationStatus = (
  locationId: string | undefined,
  { enabled }: UseCareCreditLocationStatusOptios = {}
) => {
  /**
   * Disabling the wetag queries disables subsequent queries.
   */
  const [isCareCreditPaymentsLocationEnabled, isCareCreditPaymentsLocationLoading] = PaymentsDeprecatedProp.useWetag(
    'care-credit-enabled',
    locationId,
    {
      enabled: enabled !== false,
    }
  );
  const { isEnabled: isCareCreditPaymentsOrgEnabled, isLoading: isCareCreditPaymentsOrgLoading } =
    PaymentsDeprecatedOrg.useWetagQuery('care-credit-enabled', {
      enabled: enabled !== false,
    });

  const [isCareCreditDecoupledWetagEnabled, isCareCreditDecoupledLoading] = PaymentsProp.useWetag(
    'care-credit-decoupled',
    locationId,
    {
      enabled: enabled !== false,
    }
  );

  const { firstLocation } = useMerchants(locationId);
  const { hasPayments } = firstLocation;

  const careCreditProviderQuery = useCareCreditLocationProviderStatus(
    {
      locationId,
    },
    {
      enabled:
        isCareCreditPaymentsLocationEnabled || isCareCreditPaymentsOrgEnabled || isCareCreditDecoupledWetagEnabled,
    }
  );

  const {
    isProviderConnected: isCareCreditProviderConnected,
    isProviderLoading: isCareCreditProviderLoading,
    merchantNumber,
    merchantState,
  } = careCreditProviderQuery;

  const isCareCreditPaymentsWetagEnabled = isCareCreditPaymentsLocationEnabled || isCareCreditPaymentsOrgEnabled;
  const showCareCreditPayments = isCareCreditProviderConnected && isCareCreditPaymentsWetagEnabled;
  const showCareCreditDecoupled = isCareCreditProviderConnected && !hasPayments && isCareCreditDecoupledWetagEnabled;

  return {
    /**
     * Indicates whether we can show CareCredit Payments UI
     */
    isCareCreditPaymentsWetagEnabled,
    isCareCreditPaymentsLoading: isCareCreditPaymentsLocationLoading || isCareCreditPaymentsOrgLoading,

    /**
     * Indicates whether we can show CareCredit decoupled UI
     */
    isCareCreditDecoupledWetagEnabled,
    isCareCreditDecoupledLoading,
    hasPayments,

    /**
     * CareCredit Provider Info
     */
    careCreditProviderQuery,
    isCareCreditProviderConnected,
    isCareCreditProviderLoading,
    merchantNumber,
    merchantState,

    /**
     * Show CareCredit payments UI if:
     *  A) the provider is connected
     *  B) and either the location or the organization has the care credit wetag enabled
     */
    showCareCreditPayments,

    /**
     * Show CareCredit decoupled UI if:
     *   A) the provider is connected
     *   B) and the location does not have payments enabled
     *   C) and the care credit decoupled wetag is enabled to location
     */
    showCareCreditDecoupled,

    /**
     * Can show CareCredit Payments or Decoupled CareCredit flows
     */
    showAnyCareCreditUI: showCareCreditPayments || showCareCreditDecoupled,

    /**
     * Indicates whether CareCredit payments or decoupled wetag is enabled
     */
    isAnyCareCreditWetagEnabled: isCareCreditPaymentsWetagEnabled || isCareCreditDecoupledWetagEnabled,

    /**
     * True if any query is loading (location wetag, organization wetag, decoupled wetag, or provider status)
     */
    isAnyLoading:
      isCareCreditPaymentsLocationLoading ||
      isCareCreditPaymentsOrgLoading ||
      isCareCreditDecoupledLoading ||
      isCareCreditProviderLoading,
  };
};
